/**
 * ErvaPOS - Vouchers Page (with Suspend/Cancel actions)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  Ticket,
  Gift,
  Loader2,
  Save,
  Calendar,
  Percent,
  Euro,
  Copy,
  CheckCircle,
  MoreVertical,
  Pause,
  XCircle,
  Play,
  Printer,
  Mail,
  FileText,
  Pencil
} from 'lucide-react';
import { vouchersAPI, campaignsAPI, customersAPI, settingsAPI, productsAPI } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export const VouchersPage = () => {
  const { isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vouchers, setVouchers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [campaignProductSearch, setCampaignProductSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterUsed, setFilterUsed] = useState('all');
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherCustomerSearch, setVoucherCustomerSearch] = useState('');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showVoucherPrintModal, setShowVoucherPrintModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [appSettings, setAppSettings] = useState({});
  const [saving, setSaving] = useState(false);

  const [voucherForm, setVoucherForm] = useState({
    code: '',
    value: 5,
    is_percentage: false,
    customer_id: '',
    min_purchase: 0,
    valid_until: '',
  });

  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    campaign_type: 'percentage',
    discount_value: 10,
    reward_type: 'voucher',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    min_purchase: 0,
    auto_generate_vouchers: true,
    voucher_prefix: 'PROMO',
    applicable_categories: [],
    excluded_categories: ['Medicina'],
    applicable_products: [],
    redemption_points: 0,
  });


  const normalizeSettings = (data) => {
    if (Array.isArray(data)) {
      return data.reduce((acc, item) => {
        const key = item?.key || item?.name || item?.setting_key;
        const value = item?.value ?? item?.setting_value ?? item?.content ?? '';
        if (key) acc[key] = value;
        return acc;
      }, {});
    }

    if (data && typeof data === 'object') {
      if (data.settings && typeof data.settings === 'object' && !Array.isArray(data.settings)) {
        return data.settings;
      }
      return data;
    }

    return {};
  };

  const pickSetting = (...keys) => {
    for (const key of keys) {
      const value = appSettings?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [vouchersRes, campaignsRes, customersRes, settingsRes, categoriesRes, productsRes] = await Promise.all([
        vouchersAPI.getAll({ active_only: filterUsed === 'active' }),
        campaignsAPI.getAll(false),
        customersAPI.getAll({ limit: 20000 }),
        settingsAPI.get().catch(() => ({ data: {} })),
        productsAPI.getCategories().catch(() => ({ data: [] })),
        productsAPI.getAll({ active_only: true }).catch(() => ({ data: [] })),
      ]);
      setVouchers(
        [...(vouchersRes.data || [])].sort((a, b) =>
          String(b?.created_at || '').localeCompare(String(a?.created_at || ''))
        )
      );
      setCampaigns(campaignsRes.data || []);
      setCustomers(customersRes.data || []);
      setProductCategories(
        Array.isArray(categoriesRes?.data)
          ? [...new Set(
              categoriesRes.data
                .map((c) => String(c || '').trim().toUpperCase())
                .filter(Boolean)
            )].sort((a, b) => a.localeCompare(b, 'pt', { sensitivity: 'base' }))
          : []
      );
      setProducts(Array.isArray(productsRes?.data) ? productsRes.data : []);
      setAppSettings(normalizeSettings(settingsRes?.data));
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [filterUsed]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getVoucherStatus = (voucher) =>
    String(voucher?.status || 'active').trim().toLowerCase();

  const filteredVouchers = vouchers.filter(v => {
    const normalizedQuery = normalizeSearchText(searchQuery);

    const customer = customers.find(c => c.id === v.customer_id);
    const voucherStatus = getVoucherStatus(v);

    const searchBlob = normalizeSearchText([
      v?.code,
      customer?.name,
      customer?.email,
      customer?.phone,
    ].filter(Boolean).join(' '));

    const matchesSearch = !normalizedQuery || searchBlob.includes(normalizedQuery);

    const matchesUsed = filterUsed === 'all' || 
      (filterUsed === 'active' && !v.is_used && voucherStatus !== 'cancelled' && voucherStatus !== 'suspended') ||
      (filterUsed === 'used' && v.is_used) ||
      (filterUsed === 'suspended' && voucherStatus === 'suspended') ||
      (filterUsed === 'cancelled' && voucherStatus === 'cancelled');

    return matchesSearch && matchesUsed;
  });

  const generateVoucherCode = () => {
    const code = `V${Date.now().toString(36).toUpperCase()}`;
    setVoucherForm(prev => ({ ...prev, code }));
  };

  const createVoucher = async () => {
    if (!voucherForm.code) {
      toast.error('Gere ou insira um código');
      return;
    }
    setSaving(true);
    try {
      await vouchersAPI.create({
        ...voucherForm,
        customer_id: voucherForm.customer_id || null,
      });
      toast.success('Voucher criado');
      setShowVoucherModal(false);
      setVoucherForm({ code: '', value: 5, is_percentage: false, customer_id: '', min_purchase: 0, valid_until: '' });
      loadData();
    } catch (error) {
      toast.error('Erro ao criar voucher');
    } finally {
      setSaving(false);
    }
  };

  const updateVoucherLocalStatus = (voucherId, status) => {
    setVouchers(prev =>
      prev.map(v =>
        v.id === voucherId
          ? { ...v, status, updated_at: new Date().toISOString() }
          : v
      )
    );
  };

  const suspendVoucher = async (voucher) => {
    try {
      await vouchersAPI.suspend(voucher.id);
      updateVoucherLocalStatus(voucher.id, 'suspended');
      toast.success('Voucher suspenso');
      await loadData();
    } catch (error) {
      toast.error('Erro ao suspender voucher');
    }
  };

  const cancelVoucher = async (voucher) => {
    if (!window.confirm('Tem a certeza que deseja anular este voucher? Esta ação é permanente.')) return;
    try {
      await vouchersAPI.cancel(voucher.id);
      updateVoucherLocalStatus(voucher.id, 'cancelled');
      toast.success('Voucher anulado');
      await loadData();
    } catch (error) {
      toast.error('Erro ao anular voucher');
    }
  };

  const reactivateVoucher = async (voucher) => {
    try {
      await vouchersAPI.reactivate(voucher.id);
      updateVoucherLocalStatus(voucher.id, 'active');
      toast.success('Voucher reativado');
      await loadData();
    } catch (error) {
      toast.error('Erro ao reativar voucher');
    }
  };

  
  const printVoucherWindow = async (voucher) => {
    if (!voucher) return;

    try {
      const response = await vouchersAPI.getPdf(voucher.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erro ao abrir PDF do voucher:', error);
      toast.error('Erro ao abrir PDF do voucher');
    }
  };

const printVoucher = (voucher) => {
    setSelectedVoucher(voucher);
    printVoucherWindow(voucher);
  };

  const suspendCampaign = async (campaign) => {
    try {
      await campaignsAPI.suspend(campaign.id);
      toast.success('Campanha suspensa');
      loadData();
    } catch (error) {
      toast.error('Erro ao suspender campanha');
    }
  };

  const cancelCampaign = async (campaign) => {
    if (!window.confirm('Tem a certeza que deseja anular esta campanha? Todos os vouchers associados serão também anulados.')) return;
    try {
      await campaignsAPI.cancel(campaign.id);
      toast.success('Campanha anulada');
      loadData();
    } catch (error) {
      toast.error('Erro ao anular campanha');
    }
  };

  const reactivateCampaign = async (campaign) => {
    try {
      await campaignsAPI.reactivate(campaign.id);
      toast.success('Campanha reativada');
      loadData();
    } catch (error) {
      toast.error('Erro ao reativar campanha');
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      name: '', description: '', campaign_type: 'percentage', discount_value: 10,
      reward_type: 'voucher', start_date: new Date().toISOString().split('T')[0],
      end_date: '', min_purchase: 0, applicable_categories: [],
      excluded_categories: ['Medicina'], auto_generate_vouchers: true,
      voucher_prefix: 'PROMO', applicable_products: [], redemption_points: 0,
    });
    setCampaignProductSearch('');
    setEditingCampaign(null);
  };

  const openNewCampaign = () => {
    resetCampaignForm();
    setShowCampaignModal(true);
  };

  const openEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignProductSearch('');
    setCampaignForm({
      name: campaign.name || '',
      description: campaign.description || '',
      campaign_type: campaign.campaign_type || 'percentage',
      discount_value: Number(campaign.discount_value || 0),
      reward_type: campaign.reward_type || 'voucher',
      start_date: campaign.start_date || new Date().toISOString().split('T')[0],
      end_date: campaign.end_date || '',
      min_purchase: Number(campaign.min_purchase || 0),
      auto_generate_vouchers: campaign.auto_generate_vouchers !== false,
      voucher_prefix: campaign.voucher_prefix || 'PROMO',
      applicable_categories: [...(campaign.applicable_categories || [])],
      excluded_categories: [...(campaign.excluded_categories || [])],
      applicable_products: [...(campaign.applicable_products || [])],
      redemption_points: Number(campaign.redemption_points || 0),
    });
    setShowCampaignModal(true);
  };

  const saveCampaign = async () => {
    if (!campaignForm.name || !campaignForm.end_date) {
      toast.error('Preencha nome e data de fim');
      return;
    }
    if (campaignForm.reward_type === 'product_points') {
      if (!Number.isInteger(Number(campaignForm.redemption_points)) || Number(campaignForm.redemption_points) <= 0) {
        toast.error('Indique um custo em pontos superior a zero');
        return;
      }
      if (!(campaignForm.applicable_products || []).length) {
        toast.error('Selecione pelo menos um artigo para a promoção por pontos');
        return;
      }
    }
    setSaving(true);
    try {
      if (editingCampaign) {
        await campaignsAPI.update(editingCampaign.id, campaignForm);
        toast.success('Campanha atualizada');
      } else {
        await campaignsAPI.create(campaignForm);
        toast.success('Campanha criada');
      }
      setShowCampaignModal(false);
      resetCampaignForm();
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Erro ao ${editingCampaign ? 'atualizar' : 'criar'} campanha`);
    } finally {
      setSaving(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado');
  };

  const getCustomerName = (customerId) => {
    if (!customerId) return 'Qualquer cliente';
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Desconhecido';
  };

  const getCustomerEmail = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.email || '';
  };

  const getVoucherStatusBadge = (voucher) => {
    const voucherStatus = getVoucherStatus(voucher);

    if (voucherStatus === 'cancelled') {
      return <Badge className="bg-slate-600 text-slate-300">Anulado</Badge>;
    }
    if (voucherStatus === 'suspended') {
      return <Badge className="badge-warning">Suspenso</Badge>;
    }
    if (voucher.is_used) {
      return <Badge className="bg-blue-500/20 text-blue-400">Utilizado</Badge>;
    }
    return <Badge className="badge-success">Ativo</Badge>;
  };

  const getCampaignStatusBadge = (campaign) => {
    const today = new Date().toISOString().split('T')[0];
    if (campaign.status === 'cancelled') {
      return <Badge className="bg-slate-600 text-slate-300">Anulada</Badge>;
    }
    if (campaign.status === 'suspended' || !campaign.is_active) {
      return <Badge className="badge-warning">Suspensa</Badge>;
    }
    if (campaign.start_date <= today && campaign.end_date >= today) {
      return <Badge className="badge-success">Ativa</Badge>;
    }
    if (campaign.end_date < today) {
      return <Badge className="bg-slate-600 text-slate-300">Expirada</Badge>;
    }
    return <Badge className="badge-info">Agendada</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const downloadVoucherPdf = async (voucher) => {
    try {
      const response = await vouchersAPI.getPdf(voucher.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erro ao abrir PDF do voucher:', error);
      alert('Erro ao abrir PDF do voucher');
    }
  };

  const sendVoucherByEmail = async (voucher) => {
    const suggestedEmail = getCustomerEmail(voucher.customer_id) || '';
    const email = window.prompt('Email para envio do voucher:', suggestedEmail);

    if (!email || !String(email).trim()) return;

    try {
      await vouchersAPI.sendEmail(voucher.id, String(email).trim());
      alert('Voucher enviado por email com sucesso');
    } catch (error) {
      console.error('Erro ao enviar voucher por email:', error);
      alert('Erro: ' + (error.response?.data?.detail || error.message));
    }
  };


  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Vouchers & Campanhas</h1>
          <p className="text-slate-400">Gestão de talões de desconto e promoções</p>
        </div>
        {isManager && (
          <div className="flex gap-3">
            <Button
              onClick={openNewCampaign}
              variant="outline"
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              data-testid="new-campaign-btn"
            >
              <Gift className="w-4 h-4 mr-2" />
              Nova Campanha
            </Button>
            <Button
              onClick={() => {
                generateVoucherCode();
                setShowVoucherModal(true);
              }}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="new-voucher-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Voucher
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Ticket className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Vouchers Ativos</p>
              <p className="text-2xl font-bold text-white">
                {vouchers.filter(v => !v.is_used && getVoucherStatus(v) !== 'cancelled' && getVoucherStatus(v) !== 'suspended').length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Utilizados</p>
              <p className="text-2xl font-bold text-white">
                {vouchers.filter(v => v.is_used).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Gift className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Campanhas Ativas</p>
              <p className="text-2xl font-bold text-white">
                {campaigns.filter(c => {
                  const today = new Date().toISOString().split('T')[0];
                  return c.is_active && c.status !== 'cancelled' && c.status !== 'suspended' && c.start_date <= today && c.end_date >= today;
                }).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Euro className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Valor Total</p>
              <p className="text-2xl font-bold text-white">
                €{vouchers.filter(v => !v.is_used && !v.is_percentage && getVoucherStatus(v) !== 'cancelled').reduce((sum, v) => sum + v.value, 0).toFixed(0)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="vouchers" className="space-y-4">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="vouchers" className="data-[state=active]:bg-emerald-500">
            <Ticket className="w-4 h-4 mr-2" />
            Vouchers ({vouchers.length})
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-purple-500">
            <Gift className="w-4 h-4 mr-2" />
            Campanhas ({campaigns.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vouchers" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Pesquisar código ou cliente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700"
                data-testid="search-vouchers"
              />
            </div>
            <Select value={filterUsed} onValueChange={setFilterUsed}>
              <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="used">Utilizados</SelectItem>
                <SelectItem value="suspended">Suspensos</SelectItem>
                <SelectItem value="cancelled">Anulados</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Vouchers Table */}
          <Card className="card-dark">
            <Table className="table-dark">
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Campanha</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Data origem</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVouchers.map((voucher) => (
                  <TableRow key={voucher.id} data-testid={`voucher-row-${voucher.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 bg-slate-800 rounded text-emerald-400 font-mono">
                          {voucher.code}
                        </code>
                        {voucher.voucher_type === 'return' && (
                          <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">DEV</Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyCode(voucher.code)}
                          className="h-6 w-6"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={voucher.is_percentage ? 'badge-info' : 'badge-success'}>
                        {voucher.is_percentage ? `${voucher.value}%` : `€${voucher.value.toFixed(2)}`}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {getCustomerName(voucher.customer_id)}
                    </TableCell>
                    <TableCell>
                      {voucher.campaign_id ? (
                        <Badge variant="outline" className="border-purple-500/50 text-purple-400">
                          {campaigns.find(c => c.id === voucher.campaign_id)?.name || 'Campanha'}
                        </Badge>
                      ) : voucher.voucher_type === 'return' ? (
                        <Badge variant="outline" className="border-amber-500/50 text-amber-400">
                          Devolução
                        </Badge>
                      ) : (
                        <span className="text-slate-500">Manual</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      <div className="leading-tight">
                        <div>{voucher.source_label || 'Manual'}</div>
                        {voucher.source_number ? (
                          <div className="text-xs text-slate-500">{voucher.source_number}</div>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-400">
                      {voucher.source_date
                        ? new Date(voucher.source_date).toLocaleString('pt-PT')
                        : '-'}
                    </TableCell>
                    <TableCell className="text-slate-400">
                      {voucher.valid_until || 'Sem limite'}
                    </TableCell>
                    <TableCell>
                      {getVoucherStatusBadge(voucher)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => printVoucherWindow(voucher)}>
                            <Printer className="w-4 h-4 mr-2" />
                            Imprimir Talão c/ QR
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadVoucherPdf(voucher)}>
                            <FileText className="w-4 h-4 mr-2" />
                            Descarregar PDF 80mm
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => sendVoucherByEmail(voucher)}>
                            <Mail className="w-4 h-4 mr-2" />
                            Enviar por email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => copyCode(voucher.code)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copiar Código
                          </DropdownMenuItem>
                          {!voucher.is_used && getVoucherStatus(voucher) !== 'cancelled' && (
                            <>
                              <DropdownMenuSeparator />
                              {getVoucherStatus(voucher) === 'suspended' ? (
                                <DropdownMenuItem onClick={() => reactivateVoucher(voucher)}>
                                  <Play className="w-4 h-4 mr-2" />
                                  Reativar
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => suspendVoucher(voucher)}>
                                  <Pause className="w-4 h-4 mr-2" />
                                  Suspender
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => cancelVoucher(voucher)} className="text-rose-400">
                                <XCircle className="w-4 h-4 mr-2" />
                                Anular
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredVouchers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                      <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhum voucher encontrado</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          {/* Campaigns List */}
          <div className="grid gap-4">
            {campaigns.map((campaign) => {
              const campaignVouchers = vouchers.filter(v => v.campaign_id === campaign.id);
              
              return (
                <Card key={campaign.id} className="card-dark p-6" data-testid={`campaign-${campaign.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${campaign.status !== 'cancelled' && campaign.is_active ? 'bg-purple-500/20' : 'bg-slate-700'}`}>
                        <Gift className={`w-7 h-7 ${campaign.status !== 'cancelled' && campaign.is_active ? 'text-purple-400' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{campaign.name}</h3>
                        <p className="text-sm text-slate-400">{campaign.description || 'Sem descrição'}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <Badge className={campaign.reward_type === 'product_points' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : campaign.campaign_type === 'percentage' ? 'badge-info' : 'badge-success'}>
                            {campaign.reward_type === 'product_points' ? `${campaign.redemption_points} pontos/un.` : campaign.campaign_type === 'percentage' ? `${campaign.discount_value}%` : `€${campaign.discount_value}`}
                          </Badge>
                          <Badge className={String(campaign.reward_type || 'voucher') === 'card_points' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-purple-500/20 text-purple-300 border border-purple-500/30'}>
                            {String(campaign.reward_type || 'voucher') === 'product_points' ? 'Comprar artigo com pontos' : String(campaign.reward_type || 'voucher') === 'card_points' ? 'Saldo cartão' : 'Voucher'}
                          </Badge>
                          <span className="text-sm text-slate-500">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {campaign.start_date} a {campaign.end_date}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="text-right">
                        {getCampaignStatusBadge(campaign)}
                        <p className="text-sm text-slate-400 mt-2">
                          {campaignVouchers.length} vouchers | {campaignVouchers.filter(v => v.is_used).length} utilizados
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditCampaign(campaign)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Editar campanha
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {campaign.status !== 'cancelled' && (
                            <>
                              {campaign.status === 'suspended' || !campaign.is_active ? (
                                <DropdownMenuItem onClick={() => reactivateCampaign(campaign)}>
                                  <Play className="w-4 h-4 mr-2" />
                                  Reativar
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => suspendCampaign(campaign)}>
                                  <Pause className="w-4 h-4 mr-2" />
                                  Suspender
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => cancelCampaign(campaign)} className="text-rose-400">
                                <XCircle className="w-4 h-4 mr-2" />
                                Anular Campanha
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </Card>
              );
            })}
            {campaigns.length === 0 && (
              <Card className="card-dark p-8 text-center">
                <Gift className="w-12 h-12 mx-auto mb-3 text-slate-500 opacity-50" />
                <p className="text-slate-500">Nenhuma campanha criada</p>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* New Voucher Modal */}
      <Dialog open={showVoucherModal} onOpenChange={setShowVoucherModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Criar Voucher</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Código</Label>
              <div className="flex gap-2">
                <Input
                  value={voucherForm.code}
                  onChange={(e) => setVoucherForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="bg-slate-800 border-slate-700 font-mono"
                  placeholder="CODIGO123"
                  data-testid="voucher-code"
                />
                <Button onClick={generateVoucherCode} variant="outline" className="border-slate-700">
                  Gerar
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Valor</Label>
                <Input
                  type="number"
                  min="0"
                  value={voucherForm.value}
                  onChange={(e) => setVoucherForm(prev => ({ ...prev, value: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="voucher-value"
                />
              </div>
              <div>
                <Label className="text-slate-300">Tipo</Label>
                <Select
                  value={voucherForm.is_percentage ? 'percent' : 'fixed'}
                  onValueChange={(val) => setVoucherForm(prev => ({ ...prev, is_percentage: val === 'percent' }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Valor Fixo (€)</SelectItem>
                    <SelectItem value="percent">Percentagem (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Cliente (opcional)</Label>

              <Input
                value={voucherCustomerSearch}
                onChange={(e) => setVoucherCustomerSearch(e.target.value)}
                className="bg-slate-800 border-slate-700"
                placeholder="Pesquisar cliente por nome, email ou telemóvel..."
              />

              <Select
                value={voucherForm.customer_id || "any"}
                onValueChange={(val) => setVoucherForm(prev => ({ ...prev, customer_id: val === "any" ? '' : val }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Qualquer cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Qualquer cliente</SelectItem>
                  {customers
                    .filter((c) => {
                      const q = String(voucherCustomerSearch || '').trim().toLowerCase();
                      if (!q) return true;
                      return (
                        String(c?.name || '').toLowerCase().includes(q) ||
                        String(c?.email || '').toLowerCase().includes(q) ||
                        String(c?.phone || '').toLowerCase().includes(q)
                      );
                    })
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Válido até (opcional)</Label>
              <Input
                type="date"
                value={voucherForm.valid_until}
                onChange={(e) => setVoucherForm(prev => ({ ...prev, valid_until: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                data-testid="voucher-valid-until"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoucherModal(false)} className="border-slate-700">
              Cancelar
            </Button>
            <Button onClick={createVoucher} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-voucher-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Campaign Modal */}
      <Dialog open={showCampaignModal} onOpenChange={(open) => { setShowCampaignModal(open); if (!open) resetCampaignForm(); }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingCampaign ? 'Editar Campanha' : 'Nova Campanha'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Nome da Campanha</Label>
              <Input
                value={campaignForm.name}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                placeholder="Ex: Promoção de Verão"
                data-testid="campaign-name"
              />
            </div>

            <div>
              <Label className="text-slate-300">Descrição</Label>
              <Input
                value={campaignForm.description}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                placeholder="Descrição da campanha..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Tipo de Desconto</Label>
                <Select
                  value={campaignForm.campaign_type}
                  onValueChange={(val) => setCampaignForm(prev => ({ ...prev, campaign_type: val }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentagem (%)</SelectItem>
                    <SelectItem value="fixed_value">Valor Fixo (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Valor do Desconto</Label>
                <Input
                  type="number"
                  min="0"
                  value={campaignForm.discount_value}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, discount_value: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="campaign-discount"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Como aplicar a campanha</Label>
              <Select
                value={campaignForm.reward_type || 'voucher'}
                onValueChange={(val) => setCampaignForm(prev => ({ ...prev, reward_type: val }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="voucher">Gerar voucher para próxima compra</SelectItem>
                  <SelectItem value="card_points">Creditar saldo em cartão cliente</SelectItem>
                  <SelectItem value="product_points">Comprar artigos selecionados com pontos</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Em saldo cartão, o valor da campanha é convertido em pontos. A compra por pontos permite resgatar artigos concretos por um custo fixo por unidade.
              </p>
            </div>

            {String(campaignForm.reward_type || 'voucher') === 'product_points' && (
              <div className="space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
                <div>
                  <Label className="text-amber-100">Custo por unidade em pontos</Label>
                  <Input type="number" min="1" step="1" value={campaignForm.redemption_points || ''} onChange={(e) => setCampaignForm(prev => ({ ...prev, redemption_points: Math.max(0, parseInt(e.target.value || '0', 10) || 0) }))} className="bg-slate-800 border-slate-700 mt-1" placeholder="Ex.: 500" />
                </div>
                <div>
                  <Label className="text-amber-100">Artigos abrangidos</Label>
                  <Input value={campaignProductSearch} onChange={(e) => setCampaignProductSearch(e.target.value)} className="bg-slate-800 border-slate-700 mt-1" placeholder="Pesquisar nome, referência ou código…" />
                  <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 divide-y divide-slate-800">
                    {products.filter(product => normalizeSearchText([product.name, product.reference, product.barcode].join(' ')).includes(normalizeSearchText(campaignProductSearch))).slice(0, 100).map(product => {
                      const checked = (campaignForm.applicable_products || []).includes(product.id);
                      return <label key={product.id} className="flex items-center gap-3 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 cursor-pointer"><input type="checkbox" checked={checked} onChange={(e) => setCampaignForm(prev => ({ ...prev, applicable_products: e.target.checked ? [...new Set([...(prev.applicable_products || []), product.id])] : (prev.applicable_products || []).filter(id => id !== product.id) }))} /><span className="flex-1"><b>{product.name}</b><span className="block text-xs text-slate-500">{product.reference || product.barcode || 'Sem referência'} · stock {Number(product.total_stock || 0)}</span></span></label>;
                    })}
                  </div>
                  <p className="text-xs text-amber-200/80 mt-2">{(campaignForm.applicable_products || []).length} artigo(s) selecionado(s). A promoção termina na data indicada ou deixa de estar utilizável quando não houver stock.</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Data de Início</Label>
                <Input
                  type="date"
                  value={campaignForm.start_date}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="campaign-start"
                />
              </div>
              <div>
                <Label className="text-slate-300">Data de Fim</Label>
                <Input
                  type="date"
                  value={campaignForm.end_date}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, end_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="campaign-end"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Categorias incluídas</Label>
                <div className="mt-2 max-h-40 overflow-auto rounded-lg border border-slate-700 bg-slate-800 p-3 space-y-2">
                  {productCategories.length > 0 ? productCategories.map((category) => {
                    const checked = (campaignForm.applicable_categories || []).includes(category);
                    return (
                      <label key={`inc-${category}`} className="flex items-center gap-2 text-sm text-slate-300">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setCampaignForm(prev => ({
                              ...prev,
                              applicable_categories: e.target.checked
                                ? [...(prev.applicable_categories || []), category]
                                : (prev.applicable_categories || []).filter((c) => c !== category)
                            }));
                          }}
                          className="rounded border-slate-600"
                        />
                        <span>{category}</span>
                      </label>
                    );
                  }) : (
                    <p className="text-sm text-slate-500">Sem categorias disponíveis</p>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2">Se não selecionar nenhuma, a campanha aplica-se a todas as categorias elegíveis.</p>
              </div>

              <div>
                <Label className="text-slate-300">Categorias excluídas</Label>
                <div className="mt-2 max-h-40 overflow-auto rounded-lg border border-slate-700 bg-slate-800 p-3 space-y-2">
                  {productCategories.length > 0 ? productCategories.map((category) => {
                    const checked = (campaignForm.excluded_categories || []).includes(category);
                    return (
                      <label key={`exc-${category}`} className="flex items-center gap-2 text-sm text-slate-300">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setCampaignForm(prev => ({
                              ...prev,
                              excluded_categories: e.target.checked
                                ? [...(prev.excluded_categories || []), category]
                                : (prev.excluded_categories || []).filter((c) => c !== category)
                            }));
                          }}
                          className="rounded border-slate-600"
                        />
                        <span>{category}</span>
                      </label>
                    );
                  }) : (
                    <p className="text-sm text-slate-500">Sem categorias disponíveis</p>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2">A categoria Medicina pode ficar excluída aqui, além da proteção fixa no backend.</p>
              </div>
            </div>

            {String(campaignForm.reward_type || 'voucher') === 'voucher' && (
              <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg">
                <input
                  type="checkbox"
                  id="auto_vouchers"
                  checked={campaignForm.auto_generate_vouchers}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, auto_generate_vouchers: e.target.checked }))}
                  className="rounded border-slate-700"
                />
                <Label htmlFor="auto_vouchers" className="text-slate-300 cursor-pointer">
                  Gerar vouchers automaticamente
                </Label>
              </div>
            )}

            {String(campaignForm.reward_type || 'voucher') === 'card_points' && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
                Esta campanha não cria voucher. O valor calculado é somado ao cartão do cliente em pontos, para usar numa compra futura.
              </div>
            )}

            {String(campaignForm.reward_type || 'voucher') === 'voucher' && campaignForm.auto_generate_vouchers && (
              <div>
                <Label className="text-slate-300">Prefixo dos Vouchers</Label>
                <Input
                  value={campaignForm.voucher_prefix}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, voucher_prefix: e.target.value.toUpperCase() }))}
                  className="bg-slate-800 border-slate-700 font-mono"
                  placeholder="PROMO"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Exemplo: {campaignForm.voucher_prefix}001, {campaignForm.voucher_prefix}002...
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignModal(false)} className="border-slate-700">
              Cancelar
            </Button>
            <Button onClick={saveCampaign} disabled={saving} className="bg-purple-500 hover:bg-purple-600" data-testid="create-campaign-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : editingCampaign ? <Save className="w-4 h-4 mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
              {editingCampaign ? 'Guardar Alterações' : 'Criar Campanha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voucher Print Modal */}
      <Dialog open={showVoucherPrintModal} onOpenChange={setShowVoucherPrintModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Talão de Voucher</DialogTitle>
          </DialogHeader>
          
          {selectedVoucher && (
            <div className="bg-white text-black p-4 rounded-lg font-mono text-sm text-center">
              <p className="font-bold text-lg mb-2">VOUCHER DE DESCONTO</p>
              <div className="border-t border-b border-gray-300 py-4 my-2">
                <p className="text-3xl font-bold tracking-widest">{selectedVoucher.code}</p>
              </div>
              
              {/* QR Code */}
              <div className="my-4 flex justify-center">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/vouchers/${selectedVoucher.id}/qr-image`}
                  alt="QR Code do Voucher"
                  className="w-32 h-32"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
              
              <p className="text-xl font-bold my-2">
                {selectedVoucher.is_percentage 
                  ? `${selectedVoucher.value}% DESCONTO` 
                  : `€${selectedVoucher.value.toFixed(2)} DESCONTO`}
              </p>
              {selectedVoucher.min_purchase > 0 && (
                <p className="text-xs">Compra mínima: €{selectedVoucher.min_purchase.toFixed(2)}</p>
              )}
              {selectedVoucher.valid_until && (
                <p className="text-xs mt-1">Válido até: {selectedVoucher.valid_until}</p>
              )}
              {selectedVoucher.voucher_type === 'return' && (
                <p className="text-xs mt-1 text-gray-600">(Voucher de Devolução)</p>
              )}
              <p className="text-xs mt-4 text-gray-500">
                Apresente este talão ou digitalize o QR Code
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoucherPrintModal(false)} className="border-slate-700">
              Fechar
            </Button>
            <Button onClick={() => printVoucherWindow(selectedVoucher)} className="bg-emerald-500 hover:bg-emerald-600">
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VouchersPage;
